import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentsAPI } from '../services/api';
import { useHaptic } from '../hooks/useHaptic';
import { FileText, Download, Loader2, History, Trash2 } from 'lucide-react';
import './HistoryPage.css';

function HistoryPage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const hapticTimersRef = useRef([]);

  useEffect(() => {
    fetchHistory();
    
    // Очистка таймеров при размонтировании
    return () => {
      hapticTimersRef.current.forEach(timer => clearTimeout(timer));
      hapticTimersRef.current = [];
    };
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      console.log('📋 Загружаю историю документов...');
      const response = await documentsAPI.getHistory(50, 0);
      console.log('✅ История загружена:', response.data);
      console.log('📄 Количество документов:', response.data.documents.length);
      console.log('📄 Первый документ:', response.data.documents[0]);
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('❌ Ошибка загрузки истории:', error);
    } finally {
      setLoading(false);
    }
  };

  // Рассчитываем данные для пагинации
  const totalPages = Math.ceil(documents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDocuments = documents.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Запускаем волну вибраций для новой страницы
    triggerHapticWave();
  };

  // Функция для создания волны вибраций при появлении карточек
  const triggerHapticWave = () => {
    // Очищаем предыдущие таймеры
    hapticTimersRef.current.forEach(timer => clearTimeout(timer));
    hapticTimersRef.current = [];
    
    // Создаем таймеры для каждой карточки
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, documents.length);
    const visibleCount = endIndex - startIndex;
    
    for (let i = 0; i < visibleCount; i++) {
      // Задержка соответствует задержке анимации: 0.1s + i * 0.1s
      const delay = (100 + i * 100); // в миллисекундах
      
      const timer = setTimeout(() => {
        haptic.custom('light'); // Легкая вибрация для каждой карточки
      }, delay);
      
      hapticTimersRef.current.push(timer);
    }
  };

  // Запускаем волну вибраций при изменении страницы или загрузке документов
  useEffect(() => {
    if (!loading && documents.length > 0) {
      triggerHapticWave();
    }
  }, [currentPage, loading, documents.length]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Функция для генерации компактного списка страниц с многоточием
  const getPageNumbers = () => {
    const pages = [];
    
    if (totalPages <= 5) {
      // Если страниц 5 или меньше, показываем все
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Если страниц больше 5, показываем с многоточием
      if (currentPage <= 3) {
        // Если в начале: 1 2 3 ... последняя
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Если в конце: 1 ... предпоследние страницы
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Если в середине: 1 ... текущая-1 текущая текущая+1 ... последняя
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  const handleDownload = async (doc) => {
    try {
      haptic.buttonPress();
      const response = await documentsAPI.download(doc.id);
      
      // Определяем MIME-тип на основе расширения файла
      let mimeType = 'application/octet-stream';
      const fileName = doc.original_file_name || '';
      if (fileName.toLowerCase().endsWith('.pdf')) {
        mimeType = 'application/pdf';
      } else if (fileName.toLowerCase().match(/\.(png|jpg|jpeg)$/)) {
        mimeType = fileName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
      }
      
      // Создаем Blob с правильным типом
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Очищаем URL после скачивания
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      haptic.success();
    } catch (error) {
      console.error('Ошибка скачивания:', error);
      haptic.error();
      alert('Ошибка при скачивании документа');
    }
  };

  const handleDelete = async (doc) => {
    haptic.buttonPressCritical();
    if (!window.confirm(`Вы уверены, что хотите удалить документ "${doc.original_file_name}"?`)) {
      return;
    }

    try {
      haptic.delete();
      await documentsAPI.delete(doc.id);
      
      // Обновляем список документов
      setDocuments(prevDocs => {
        const newDocs = prevDocs.filter(d => d.id !== doc.id);
        
        // Если после удаления текущая страница пуста и это не первая страница
        const newTotalPages = Math.ceil(newDocs.length / itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
        
        return newDocs;
      });
      
      haptic.success();
      // Показываем уведомление об успехе (если есть Telegram WebApp)
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showAlert('Документ успешно удален');
      }
    } catch (error) {
      console.error('Ошибка удаления:', error);
      haptic.error();
      alert('Ошибка при удалении документа');
    }
  };

  if (loading) {
    return (
      <div className="container flex flex-col items-center justify-center" style={{ minHeight: '100vh' }}>
        <Loader2 className="loader-icon" />
        <p className="mt-16">Загрузка истории...</p>
      </div>
    );
  }

  console.log('🎨 Рендерю HistoryPage');
  console.log('📊 Всего документов:', documents.length);
  console.log('📄 Текущая страница:', currentPage);
  console.log('📄 Документы на текущей странице:', currentDocuments.length);
  console.log('📄 Документы для отображения:', currentDocuments);

  return (
    <div className="history-page container">
      <h1 className="title animate-fade-in">История документов</h1>

      {documents.length === 0 ? (
        <div className="empty-state animate-scale-in animate-delay-100">
          <History size={64} className="empty-icon" />
          <h3>История пуста</h3>
          <p>Вы еще не подписали ни одного документа</p>
          <button className="button mt-16" onClick={() => {
            haptic.navigation();
            navigate('/');
          }}>
            Подписать документ
          </button>
        </div>
      ) : (
        <>
          <div className="documents-list">
            {currentDocuments.map((doc, index) => {
              console.log('🔵 Рендерю документ:', doc.id, doc.original_file_name);
              console.log('🔵 signed_file_id:', doc.signed_file_id);
              return (
                <div 
                  key={doc.id} 
                  className="document-card stagger-item"
                  style={{ animationDelay: `${0.1 + index * 0.1}s` }}
                >
                  <button
                    className="button button-danger delete-button"
                    onClick={() => {
                      console.log('🗑️ Нажата кнопка удаления для:', doc.id);
                      handleDelete(doc);
                    }}
                    title="Удалить документ"
                  >
                    <Trash2 size={20} />
                  </button>
                  
                  <div className="document-info">
                    <div className="document-icon">
                      <FileText size={24} />
                    </div>
                    <div className="document-details">
                      <h3>{doc.original_file_name}</h3>
                      <p className="document-date">
                        {new Date(doc.created_at).toLocaleString('ru-RU')}
                      </p>
                      <span className={`document-status ${doc.signed_file_id ? 'signed' : 'pending'}`}>
                        {doc.signed_file_id ? 'Подписан' : 'Обработка...'}
                      </span>
                    </div>
                  </div>
                  <div className="document-actions">
                    {doc.signed_file_id && (
                      <button
                        className="button button-secondary download-button"
                        onClick={() => handleDownload(doc)}
                      >
                        <Download size={18} />
                        Скачать
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination-fixed">
              <div className="pagination">
                <button
                  className="pagination-button"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  ←
                </button>
                
                <div className="pagination-pages">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                <button
                  className="pagination-button"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HistoryPage;

